import React, { useCallback, useMemo, useState } from 'react';
import type { Action } from '@shared/helpers/pipeline/update-engine.models';
import type { Baseline } from '@shared/models/project.model';
import {
  getActionNumber,
  getAffectedElementIds,
} from '@shared/helpers/actions/action.helpers';
import {
  getElementById,
  isElement,
} from '@shared/helpers/recursive-element.helpers';
import { Icon } from '@ui/Icon';
import { HideOutsideViewport } from '@ui/HideOutsideViewport';
import { Page } from '@client/store/ui/ui.model';
import ElementTreeRow from './ElementTreeRow';
import ElementListRow, { ELEMENT_LIST_ROW_HEIGHT } from './ElementListRow';

const ACTION_ICON_SIZE = 12;

const ActionIcon = <Icon category="ui" icon="action" size={ACTION_ICON_SIZE} />;

type ActionListRowProps = {
  action: Action;
  version: Baseline;
  selectedRow: string | null;
};

export const ActionListRow: React.FC<ActionListRowProps> = ({
  action,
  version,
  selectedRow,
}) => {
  const [expanded, setExpanded] = useState(false);

  const actionNumber = getActionNumber(action.id, version);
  const namePlaceholder =
    actionNumber > 0 ? `Action ${actionNumber}` : 'Action';

  const affectedElements = useMemo(
    () =>
      getAffectedElementIds(action.changes)
        .map((id) => getElementById(version, id, false))
        .filter(isElement),
    [action.changes, version],
  );

  const expandable = affectedElements.length > 0;

  const handleExpandedChange = useCallback((next: boolean) => {
    setExpanded(next);
  }, []);

  return (
    <>
      <HideOutsideViewport height={ELEMENT_LIST_ROW_HEIGHT}>
        <ElementListRow
          name={action.name ?? ''}
          namePlaceholder={namePlaceholder}
          iconSlot={ActionIcon}
          hideQuantity
          resizable={false}
          leftGap={4}
          expandable={expandable}
          expanded={expanded}
          onExpandedChange={handleExpandedChange}
          nameReadonly
        />
      </HideOutsideViewport>

      {expanded &&
        affectedElements.map((element) => (
          <ElementTreeRow
            key={element.id}
            element={element}
            depth={2}
            selectedRow={selectedRow}
            onSelect={() => undefined}
            selectionPage={Page.Elements}
          />
        ))}
    </>
  );
};
